'use strict'

// Pure planning for automation types that send mail, write spreadsheet rows,
// receive a webhook, or save an HTTP response. HTTP and SQLite stay in the
// app server. The designer imports the same defaults and variable names.

const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/
const TEMPLATE = /\{\{(\w+)\}\}/g
const SKIP_WRITE_TYPES = new Set(['password', 'image', 'file', 'formula', 'relationship', 'signature', 'sequence'])
const SYSTEM_COLUMNS = new Set(['id', 'createdAt', 'updatedAt'])
const OPERATIONS = new Set(['create', 'update', 'upsert'])
const MAX_WRITE_ROWS = 100
const PLAYWRIGHT_STEP_TYPES = ['navigate', 'click', 'type', 'select', 'wait', 'screenshot', 'assert_text', 'assert_visible']

const AUTOMATION_TYPE_OPTIONS = [
  { value: 'http_request', text: 'HTTP Request' },
  { value: 'playwright', text: 'Playwright' },
  { value: 'send_email', text: 'Send Email' },
  { value: 'write_records', text: 'Write Records' },
  { value: 'webhook', text: 'Webhook' }
]

function isIdentifier (value) {

  return typeof value === 'string' && FIELD_NAME.test(value)
}

function clone (value) {

  return JSON.parse(JSON.stringify(value))
}

function isPlainObject (value) {

  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function defaultAutomationConfig (type) {

  if (type === 'http_request') {

    return {
      method: 'GET',
      url: '',
      headers: [{ key: '', value: '', enabled: true, variableName: '' }],
      queryParams: [{ key: '', value: '', enabled: true, variableName: '' }],
      body: {
        type: 'none',
        raw: '',
        formData: [{ key: '', value: '', enabled: true, variableName: '' }]
      },
      responseMap: {
        enabled: false,
        target: 'spreadsheet',
        spreadSheetUniqueTitle: '',
        operation: 'create',
        matchColumn: '',
        columnMap: []
      }
    }
  }

  if (type === 'playwright') {

    return {
      startUrl: '',
      steps: [],
      viewport: { width: 1280, height: 720 },
      headless: true
    }
  }

  if (type === 'send_email') {

    return {
      mailAccountId: '',
      to: '',
      subject: '',
      body: ''
    }
  }

  if (type === 'write_records') {

    return {
      spreadSheetUniqueTitle: '',
      operation: 'create',
      matchColumn: '',
      columnMap: []
    }
  }

  if (type === 'webhook') {

    return {
      secretHeader: 'X-Webhook-Secret',
      secret: '',
      fieldMap: [],
      write: {
        spreadSheetUniqueTitle: '',
        operation: 'create',
        matchColumn: '',
        columnMap: []
      }
    }
  }

  return {}
}

function mergeMissing (target, defaults) {

  for (const key of Object.keys(defaults)) {

    const value = defaults[key]

    if (target[key] === undefined || target[key] === null) {

      target[key] = clone(value)
      continue
    }

    if (isPlainObject(value) && isPlainObject(target[key])) {

      mergeMissing(target[key], value)
    }
  }
}

function ensureAutomationConfig (automation) {

  if (!automation || typeof automation !== 'object') return automation

  const defaults = defaultAutomationConfig(automation.type)

  if (!isPlainObject(automation.config)) {

    automation.config = defaults
    return automation
  }

  mergeMissing(automation.config, defaults)

  if (automation.type === 'http_request') {

    for (const entry of automation.config.queryParams || []) {

      if (entry && entry.variableName === undefined) entry.variableName = ''
    }

    for (const entry of automation.config.headers || []) {

      if (entry && entry.variableName === undefined) entry.variableName = ''
    }

    const formData = automation.config.body && automation.config.body.formData

    for (const entry of formData || []) {

      if (entry && entry.variableName === undefined) entry.variableName = ''
    }
  }

  return automation
}

function templateValue (value) {

  if (value === undefined || value === null) return null
  if (typeof value === 'object') return JSON.stringify(value)

  return String(value)
}

function applyTemplate (text, variables) {

  const source = text == null ? '' : String(text)
  const vars = variables || {}

  return source.replace(TEMPLATE, (match, name) => {

    const next = templateValue(vars[name])

    return next === null ? match : next
  })
}

function templateNames (text) {

  const names = []
  const source = text == null ? '' : String(text)

  source.replace(TEMPLATE, (match, name) => {

    if (isIdentifier(name) && !names.includes(name)) names.push(name)

    return match
  })

  return names
}

function readJsonPath (value, path) {

  if (typeof path !== 'string' || !path.trim()) return { found: false, value: undefined }

  let current = value

  for (const part of path.split('.')) {

    if (!part) return { found: false, value: undefined }
    if (current == null || typeof current !== 'object') return { found: false, value: undefined }

    if (Array.isArray(current)) {

      if (!/^\d+$/.test(part)) return { found: false, value: undefined }

      const index = Number(part)

      if (index >= current.length) return { found: false, value: undefined }

      current = current[index]
      continue
    }

    if (!Object.prototype.hasOwnProperty.call(current, part)) return { found: false, value: undefined }

    current = current[part]
  }

  return { found: true, value: current }
}

function headerValue (headers, name) {

  if (!headers || !name) return ''

  const wanted = String(name).toLowerCase()

  for (const key of Object.keys(headers)) {

    if (String(key).toLowerCase() === wanted) {

      const value = headers[key]

      return value == null ? '' : String(value)
    }
  }

  return ''
}

function secretsMatch (provided, expected) {

  if (!expected) return false

  const actual = String(provided || '')
  const wanted = String(expected)
  const length = Math.max(actual.length, wanted.length)
  let diff = actual.length === wanted.length ? 0 : 1

  for (let i = 0; i < length; i++) {

    const left = actual.charCodeAt(i)
    const right = wanted.charCodeAt(i)

    diff |= (left === left ? left : 0) ^ (right === right ? right : 0)
  }

  return diff === 0
}

function pushVariable (list, name, location) {

  if (!isIdentifier(name)) return
  if (list.some((item) => item.variableName === name)) return

  list.push({ variableName: name, location })
}

function automationVariableNames (automation) {

  const list = []
  const config = (automation && automation.config) || {}

  for (const entry of config.queryParams || []) pushVariable(list, entry && entry.variableName, 'param')
  for (const entry of config.headers || []) pushVariable(list, entry && entry.variableName, 'header')
  for (const entry of (config.body && config.body.formData) || []) pushVariable(list, entry && entry.variableName, 'body')
  for (const name of templateNames(config.url)) pushVariable(list, name, 'url')
  for (const name of templateNames(config.body && config.body.raw)) pushVariable(list, name, 'body-raw')

  if (automation && automation.type === 'send_email') {

    for (const name of templateNames(config.to)) pushVariable(list, name, 'to')
    for (const name of templateNames(config.subject)) pushVariable(list, name, 'subject')
    for (const name of templateNames(config.body)) pushVariable(list, name, 'body-text')
  }

  if (automation && automation.type === 'write_records') {

    for (const entry of config.columnMap || []) {

      if (entry && (entry.source === 'variable' || (!entry.source && entry.variableName))) {

        pushVariable(list, entry.variableName, 'column')
      }
    }
  }

  return list
}

function writableColumns (columns) {

  return (columns || []).filter((column) => {

    return column && isIdentifier(column.uniqueTitle) && !SKIP_WRITE_TYPES.has(column.type)
  })
}

function columnsByTitle (columns) {

  const map = new Map()

  for (const column of columns || []) {

    if (column && column.uniqueTitle) map.set(column.uniqueTitle, column)
  }

  return map
}

function entrySource (entry) {

  if (entry.source === 'variable' || entry.source === 'static' || entry.source === 'json') return entry.source
  if (entry.jsonPath) return 'json'
  if (entry.variableName) return 'variable'

  return 'static'
}

function coerceColumnValue (column, value) {

  if (value === null) return { value: null }

  if (column.type === 'number') {

    if (typeof value === 'string' && value.trim() === '') return { error: `${column.uniqueTitle} must be a number` }
    if (typeof value === 'object') return { error: `${column.uniqueTitle} must be a number` }

    const number = typeof value === 'number' ? value : Number(String(value).trim())

    if (!Number.isFinite(number)) return { error: `${column.uniqueTitle} must be a number` }

    return { value: number }
  }

  if (typeof value === 'object') return { value: JSON.stringify(value) }

  return { value: String(value) }
}

function resolveEntry (entry, context) {

  const source = entrySource(entry)

  if (source === 'static') {

    return { found: true, value: applyTemplate(entry.staticValue, context.variables) }
  }

  if (source === 'variable') {

    if (!isIdentifier(entry.variableName)) return { error: 'variable name is invalid' }

    const variables = context.variables || {}

    if (!Object.prototype.hasOwnProperty.call(variables, entry.variableName)) return { found: false }

    return { found: true, value: variables[entry.variableName] }
  }

  return readJsonPath(context.json, entry.jsonPath)
}

function planColumnValues (columnMap, context, columns) {

  const values = {}
  const skipped = []
  const known = columnsByTitle(columns)

  for (const entry of columnMap || []) {

    if (!entry || !entry.columnUniqueTitle) continue
    if (!isIdentifier(entry.columnUniqueTitle)) return { error: `invalid column ${entry.columnUniqueTitle}` }
    if (SYSTEM_COLUMNS.has(entry.columnUniqueTitle)) continue

    const column = known.get(entry.columnUniqueTitle)

    if (!column) return { error: `unknown column ${entry.columnUniqueTitle}` }
    if (SKIP_WRITE_TYPES.has(column.type)) {

      skipped.push(entry.columnUniqueTitle)
      continue
    }

    const resolved = resolveEntry(entry, context || {})

    if (resolved.error) return resolved
    if (!resolved.found) continue

    const coerced = coerceColumnValue(column, resolved.value)

    if (coerced.error) return coerced

    values[entry.columnUniqueTitle] = coerced.value
  }

  return { values, skipped }
}

function planRecordWrite (config, context, columns) {

  const source = config || {}
  const operation = source.operation || 'create'

  if (!OPERATIONS.has(operation)) return { error: `unknown operation ${operation}` }
  if (!isIdentifier(source.spreadSheetUniqueTitle)) return { error: 'spreadsheet is required' }

  const planned = planColumnValues(source.columnMap, context, columns)

  if (planned.error) return planned

  const values = planned.values
  let matchColumn = ''
  let matchValue

  if (operation !== 'create') {

    matchColumn = source.matchColumn || ''

    if (!matchColumn) return { error: 'update and upsert need a match column' }
    if (matchColumn !== 'id' && !isIdentifier(matchColumn)) return { error: 'invalid match column' }

    if (matchColumn !== 'id') {

      const column = columnsByTitle(columns).get(matchColumn)

      if (!column) return { error: `unknown match column ${matchColumn}` }
      if (SKIP_WRITE_TYPES.has(column.type)) return { error: `cannot match on ${matchColumn}` }
      if (!Object.prototype.hasOwnProperty.call(values, matchColumn)) {

        return { error: `match column ${matchColumn} has no value` }
      }

      matchValue = values[matchColumn]
    } else {

      const record = context && context.record

      if (record && record.id != null) {

        matchValue = record.id
      } else {

        const idEntry = (source.columnMap || []).find((entry) => entry && entry.columnUniqueTitle === 'id')

        if (!idEntry) return { error: 'match column id has no value' }

        const resolved = resolveEntry(idEntry, context || {})

        if (resolved.error) return resolved
        if (!resolved.found) return { error: 'match column id has no value' }

        matchValue = resolved.value
      }
    }

    if (matchColumn === 'id') {

      const id = Number(matchValue)

      if (!Number.isInteger(id) || id < 1) return { error: 'match id must be a positive integer' }

      matchValue = id
    }

    if (matchValue === undefined || matchValue === null || matchValue === '') {

      return { error: `match column ${matchColumn} has no value` }
    }
  }

  delete values.id
  delete values.createdAt
  delete values.updatedAt

  if (!Object.keys(values).length) return { error: 'no values to write' }

  return {
    spreadSheetUniqueTitle: source.spreadSheetUniqueTitle,
    operation,
    matchColumn,
    matchValue,
    values,
    skipped: planned.skipped
  }
}

function jsonRows (body, target) {

  if (Array.isArray(body)) {

    if (target === 'trigger_record') return { error: 'a trigger record update needs one JSON object' }
    if (body.length > MAX_WRITE_ROWS) return { error: `response has ${body.length} rows; the limit is ${MAX_WRITE_ROWS}` }
    if (!body.length) return { error: 'response array is empty' }

    for (const row of body) {

      if (!isPlainObject(row)) return { error: 'each response row must be a JSON object' }
    }

    return { rows: body }
  }

  if (!isPlainObject(body)) return { error: 'response JSON must be an object' }

  return { rows: [body] }
}

function responseColumnMap (columnMap) {

  return (columnMap || []).map((entry) => ({
    columnUniqueTitle: entry && entry.columnUniqueTitle,
    source: 'json',
    jsonPath: entry && entry.jsonPath
  }))
}

function planResponseWrites (responseMap, body, context, columns) {

  if (!responseMap || !responseMap.enabled) return { skipped: true, plans: [] }

  const target = responseMap.target === 'trigger_record' ? 'trigger_record' : 'spreadsheet'
  const parsed = jsonRows(body, target)

  if (parsed.error) return parsed

  const record = context && context.record
  const config = target === 'trigger_record'
    ? {
      spreadSheetUniqueTitle: record && record.spreadSheetUniqueTitle,
      operation: 'update',
      matchColumn: 'id',
      columnMap: responseColumnMap(responseMap.columnMap)
    }
    : {
      spreadSheetUniqueTitle: responseMap.spreadSheetUniqueTitle,
      operation: responseMap.operation || 'create',
      matchColumn: responseMap.matchColumn || '',
      columnMap: responseColumnMap(responseMap.columnMap)
    }

  if (target === 'trigger_record' && (!record || record.id == null)) {

    return { error: 'no triggering record was provided' }
  }

  const plans = []

  for (const row of parsed.rows) {

    const plan = planRecordWrite(config, { variables: context && context.variables, json: row, record }, columns)

    if (plan.error) return { error: plan.error }

    plans.push(plan)
  }

  return { plans, spreadSheetUniqueTitle: config.spreadSheetUniqueTitle }
}

function planWebhook (config, body, columns) {

  if (!isPlainObject(body)) return { error: 'webhook body must be a JSON object' }

  const variables = {}

  for (const field of (config && config.fieldMap) || []) {

    if (!field || !field.variableName) continue
    if (!isIdentifier(field.variableName)) return { error: `invalid variable ${field.variableName}` }

    const read = readJsonPath(body, field.jsonPath)

    if (read.found) variables[field.variableName] = read.value
  }

  const write = (config && config.write) || {}

  if (!write.spreadSheetUniqueTitle) return { variables, plans: [] }

  const plan = planRecordWrite(write, { variables, json: body }, columns)

  if (plan.error) return { error: plan.error, variables }

  return { variables, plans: [plan] }
}

function planEmail (config, variables) {

  const to = applyTemplate(config && config.to, variables).trim()
  const subject = applyTemplate(config && config.subject, variables).trim()
  const text = applyTemplate(config && config.body, variables)

  if (!to) return { error: 'email to is required' }

  const recipients = to.split(',').map((item) => item.trim()).filter(Boolean)

  if (!recipients.length || recipients.some((item) => !item.includes('@'))) {

    return { error: 'email to must be an email address' }
  }

  if (!subject) return { error: 'email subject is required' }

  return { to: recipients.join(', '), subject, text }
}

const MAIL_CONNECTION_MODES = [
  { value: 'starttls', label: 'STARTTLS', detail: 'Port 587. The connection upgrades to TLS.', port: 587 },
  { value: 'tls', label: 'Implicit TLS', detail: 'Port 465. The connection starts encrypted.', port: 465 }
]

function createMailAccountId () {

  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let suffix = ''

  for (let index = 0; index < 8; index += 1) suffix += chars[Math.floor(Math.random() * chars.length)]

  return `mail_${suffix}`
}

function mailPortForMode (mode) {

  return mode === 'tls' ? 465 : 587
}

function blankMailAccount () {

  return {
    id: createMailAccountId(),
    name: '',
    host: '',
    port: 587,
    mode: 'starttls',
    user: '',
    password: '',
    from: ''
  }
}

function nextMailAccountName (rawName, usedNames) {

  const trimmed = String(rawName || '').trim()

  if (trimmed) {

    usedNames.add(trimmed)

    return trimmed
  }

  let name = 'Email'
  let count = 2

  while (usedNames.has(name)) {

    name = `Email ${count}`
    count += 1
  }

  usedNames.add(name)

  return name
}

function normalizeMailAccount (raw, usedIds, usedNames) {

  if (!isPlainObject(raw)) return { error: 'each email account must be an object' }

  let id = String(raw.id || '').trim()

  if (!isIdentifier(id) || usedIds.has(id)) id = createMailAccountId()

  while (usedIds.has(id)) id = createMailAccountId()

  usedIds.add(id)

  const mode = raw.mode === 'tls' ? 'tls' : 'starttls'
  const port = raw.port === '' || raw.port == null ? mailPortForMode(mode) : Number(raw.port)

  if (!Number.isInteger(port) || port < 1 || port > 65535) return { error: 'mail.port must be a port number' }

  return {
    account: {
      id,
      name: nextMailAccountName(raw.name, usedNames),
      host: String(raw.host || '').trim(),
      port,
      mode,
      user: String(raw.user || ''),
      password: String(raw.password || ''),
      from: String(raw.from || '').trim()
    }
  }
}

function legacyMailAccounts (mail) {

  const secure = mail.secure === true || mail.secure === 'true' || mail.secure === 1 || mail.secure === '1'
  const hasLegacy = ['host', 'user', 'password', 'from'].some((key) => String(mail[key] || '').trim())
    || (mail.port != null && mail.port !== '')
    || secure

  if (!hasLegacy) return []

  const mode = secure || Number(mail.port) === 465 ? 'tls' : 'starttls'

  return [{
    id: 'mail_primary',
    name: 'Email',
    host: mail.host,
    port: mail.port == null || mail.port === '' ? mailPortForMode(mode) : mail.port,
    mode,
    user: mail.user,
    password: mail.password,
    from: mail.from
  }]
}

function normalizeMailSettings (mail) {

  if (mail == null) return { mail: null }
  if (!isPlainObject(mail)) return { error: 'mail must be an object' }
  if (mail.accounts != null && !Array.isArray(mail.accounts)) return { error: 'mail.accounts must be a list' }

  const usedIds = new Set()
  const usedNames = new Set()
  const accounts = []
  const sourceAccounts = Array.isArray(mail.accounts) ? mail.accounts : legacyMailAccounts(mail)

  for (const raw of sourceAccounts) {

    const normalized = normalizeMailAccount(raw, usedIds, usedNames)

    if (normalized.error) return normalized

    accounts.push(normalized.account)
  }

  let defaultAccountId = String(mail.defaultAccountId || '').trim()

  if (!accounts.some((account) => account.id === defaultAccountId)) {

    defaultAccountId = accounts.length ? accounts[0].id : ''
  }

  return { mail: { accounts, defaultAccountId } }
}

function restoreMailPasswords (mail, existingSettings) {

  if (!mail || !Array.isArray(mail.accounts)) return mail

  const stored = existingSettings && existingSettings.mail && typeof existingSettings.mail === 'object'
    ? existingSettings.mail
    : null

  if (!stored) return mail

  const previous = new Map()
  const accounts = Array.isArray(stored.accounts) ? stored.accounts : []

  for (const account of accounts) {

    if (account && account.id && account.password) previous.set(account.id, account.password)
  }

  const legacyPassword = !accounts.length && stored.password ? String(stored.password) : ''

  for (const account of mail.accounts) {

    if (account.password) continue
    if (previous.has(account.id)) account.password = previous.get(account.id)
    else if (legacyPassword && mail.accounts.length === 1) account.password = legacyPassword
  }

  return mail
}

function resolveMailAccount (mail, accountId) {

  const normalized = normalizeMailSettings(mail)

  if (normalized.error) return normalized
  if (!normalized.mail || !normalized.mail.accounts.length) return { error: 'SMTP host is not configured' }

  const wanted = String(accountId || '').trim() || normalized.mail.defaultAccountId
  const account = normalized.mail.accounts.find((item) => item.id === wanted)

  if (!account) return { error: 'email account was not found' }
  if (!account.host) return { error: 'SMTP host is not configured' }
  if (!account.from) return { error: 'SMTP from address is not configured' }

  return { account }
}

function normalizeTriggerRecord (record) {

  if (!isPlainObject(record)) return null

  const id = Number(record.id)

  if (!Number.isInteger(id) || id < 1) return null
  if (!isIdentifier(record.spreadSheetUniqueTitle)) return null

  return { id, spreadSheetUniqueTitle: record.spreadSheetUniqueTitle }
}

function webhookPath (applicationUniqueTitle, automationUniqueTitle) {

  return `/webhooks/${applicationUniqueTitle}/${automationUniqueTitle}`
}

function buildHttpRequest (config, variables) {

  const source = JSON.parse(JSON.stringify(config || {}))

  if (!source.url) return { error: 'automation has no URL configured' }

  if (variables) {

    for (const param of source.queryParams || []) {

      if (param.variableName && variables[param.variableName] !== undefined) param.value = variables[param.variableName]
    }

    for (const header of source.headers || []) {

      if (header.variableName && variables[header.variableName] !== undefined) header.value = variables[header.variableName]
    }

    for (const field of (source.body && source.body.formData) || []) {

      if (field.variableName && variables[field.variableName] !== undefined) field.value = variables[field.variableName]
    }

    source.url = applyTemplate(source.url, variables)

    if (source.body && source.body.raw) source.body.raw = applyTemplate(source.body.raw, variables)
  }

  const headers = {}

  for (const header of source.headers || []) {

    if (header.enabled && header.key) headers[header.key] = header.value
  }

  let url = source.url
  const enabledParams = (source.queryParams || []).filter((param) => param.enabled && param.key)

  if (enabledParams.length) {

    const separator = url.includes('?') ? '&' : '?'
    const paramString = enabledParams.map((param) => `${encodeURIComponent(param.key)}=${encodeURIComponent(param.value)}`).join('&')

    url = `${url}${separator}${paramString}`
  }

  let data
  let body = ''
  const method = source.method || 'GET'

  if (source.body && source.body.type !== 'none' && method !== 'GET') {

    if (source.body.type === 'json') {

      body = source.body.raw || ''

      try {

        data = JSON.parse(body)
      } catch (error) {

        data = body
      }

      if (!headers['Content-Type'] && !headers['content-type']) headers['Content-Type'] = 'application/json'
    }

    if (source.body.type === 'form-data' && source.body.formData) {

      const form = {}

      for (const field of source.body.formData.filter((entry) => entry.enabled && entry.key)) {

        form[field.key] = field.value
      }

      data = form
      body = JSON.stringify(form)

      if (!headers['Content-Type'] && !headers['content-type']) headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }
  }

  return { method, url, headers, data, body }
}

function playwrightSteps (config) {

  const steps = config && config.steps

  if (!Array.isArray(steps) || !steps.length) return { error: 'automation has no steps configured' }

  const enabled = steps.filter((step) => step && step.enabled)

  if (!enabled.length) return { error: 'automation has no enabled steps' }

  for (const step of enabled) {

    if (!PLAYWRIGHT_STEP_TYPES.includes(step.type)) return { error: `unknown step type: ${step.type}` }
  }

  return { steps: enabled }
}

function automationTypeLabel (type) {

  const match = AUTOMATION_TYPE_OPTIONS.find((option) => option.value === type)

  return match ? match.text : (type || '')
}

const AUTOMATION_TYPE_HELP = {
  http_request: 'Call a URL. You can save the JSON response into a spreadsheet or onto the submitted record.',
  playwright: 'Drive a browser: open a page, click, type, and check what is on the screen.',
  send_email: 'Send an email through an account saved in app settings.',
  write_records: 'Create or update rows in a spreadsheet.',
  webhook: 'Receive a JSON POST from another system and write it into a spreadsheet.'
}

const AUTOMATION_TYPE_SHORT = {
  http_request: 'HTTP',
  playwright: 'Browser',
  send_email: 'Email',
  write_records: 'Records',
  webhook: 'Webhook'
}

const VARIABLE_LOCATIONS = {
  param: 'query param',
  header: 'header',
  body: 'form field',
  url: 'URL',
  'body-raw': 'JSON body',
  to: 'recipient',
  subject: 'subject',
  'body-text': 'message',
  column: 'column'
}

function automationTypeHelp (type) {

  return AUTOMATION_TYPE_HELP[type] || ''
}

function automationTypeShort (type) {

  return AUTOMATION_TYPE_SHORT[type] || automationTypeLabel(type)
}

function variableLocationLabel (location) {

  return VARIABLE_LOCATIONS[location] || location || ''
}

function summarizeAutomationRun (entry) {

  if (!entry) return ''

  if (entry.status === 'error') return entry.error || 'The automation failed.'

  const response = entry.response || {}

  if (response.messageId) return 'Email sent.'

  const write = entry.recordWrite && entry.recordWrite.recordsAffected != null
    ? entry.recordWrite
    : (response.recordsAffected != null ? response : null)

  if (write) {

    const count = write.recordsAffected

    if (!count) return 'No records were written.'
    if (count === 1) return '1 record written.'

    return `${count} records written.`
  }

  const steps = entry.playwrightResults && entry.playwrightResults.stepResults

  if (Array.isArray(steps)) {

    const failed = steps.find((step) => step.status !== 'success')

    if (failed) return `Browser script failed on step ${failed.index + 1}.`

    return `Browser script passed ${steps.length} steps.`
  }

  if (response.status) return `HTTP ${response.status} ${response.statusText || ''}`.trim()

  return 'Automation finished.'
}

function visitAutomationForms (node, page, automationUniqueTitle, found) {

  if (!node || typeof node !== 'object') return

  const actions = node.formActions && node.formActions.actions

  if (Array.isArray(actions) && actions.some((action) => action && action.automationUniqueTitle === automationUniqueTitle)) {

    found.push({
      kind: 'form',
      page: (page && (page.title || page.uniqueTitle)) || '',
      name: node.title || node.uniqueTitle || 'form'
    })
  }

  for (const child of node.components || []) visitAutomationForms(child, page, automationUniqueTitle, found)
}

function automationUsage (automationUniqueTitle, pages, schedules) {

  const uses = []

  for (const page of pages || []) visitAutomationForms(page, page, automationUniqueTitle, uses)

  for (const schedule of schedules || []) {

    if (schedule && schedule.automationUniqueTitle === automationUniqueTitle) {

      uses.push({ kind: 'schedule', name: schedule.title || 'schedule' })
    }
  }

  return uses
}

const BULK_SKIP_FIELD_TYPES = new Set(['password', 'image', 'file', 'signature', 'sequence'])

function automationFieldNames (automation, columns) {

  const columnsByName = columnsByTitle(columns)
  const names = []

  for (const item of automationVariableNames(automation)) {

    const column = columnsByName.get(item.variableName)

    if (!column || BULK_SKIP_FIELD_TYPES.has(column.type)) continue
    if (!names.includes(item.variableName)) names.push(item.variableName)
  }

  return names
}

function automationUsesRecordId (automation) {

  return automationVariableNames(automation).some((item) => item.variableName === 'id')
}

function automationSheetUse (automation, spreadSheetUniqueTitle, columns) {

  if (!automation || automation.type === 'webhook') return null
  if (!isIdentifier(spreadSheetUniqueTitle)) return null

  const config = automation.config || {}
  const fields = automationFieldNames(automation, columns)
  const usesId = automationUsesRecordId(automation)

  if (automation.type === 'write_records' && config.spreadSheetUniqueTitle === spreadSheetUniqueTitle) {

    const operation = config.operation === 'update' || config.operation === 'upsert' ? config.operation : 'create'
    const summary = operation !== 'create' && config.matchColumn === 'id'
      ? 'Updates each selected record on this spreadsheet.'
      : operation === 'create'
        ? 'Creates a record on this spreadsheet for each selected record.'
        : 'Writes this spreadsheet once for each selected record.'

    return { summary, fields }
  }

  const map = config.responseMap

  if (map && map.enabled && map.target === 'trigger_record') {

    return { summary: 'Saves the response onto each selected record.', fields }
  }

  if (map && map.enabled && map.spreadSheetUniqueTitle === spreadSheetUniqueTitle) {

    return { summary: 'Writes the response onto this spreadsheet for each selected record.', fields }
  }

  if (!fields.length && !usesId) return null

  const summary = fields.length
    ? `Fills ${fields.join(', ')} from each selected record.`
    : 'Uses the id of each record.'

  return { summary, fields }
}

function automationRecordVariables (automation, record, columns) {

  const variables = {}

  if (!record || typeof record !== 'object') return variables

  const columnsByName = columnsByTitle(columns)

  for (const name of automationFieldNames(automation, columns)) {

    const value = record[name]

    if (value === undefined || value === null || value === '') continue

    const column = columnsByName.get(name)

    if (column && column.type === 'multiselect' && Array.isArray(value)) {

      if (value.length) variables[name] = value.join(', ')
      continue
    }

    if (typeof value === 'object') continue

    variables[name] = value
  }

  if (automationUsesRecordId(automation) && record.id != null) variables.id = record.id

  return variables
}

module.exports = {
  FIELD_NAME,
  MAX_WRITE_ROWS,
  AUTOMATION_TYPE_OPTIONS,
  SKIP_WRITE_TYPES,
  isIdentifier,
  defaultAutomationConfig,
  ensureAutomationConfig,
  applyTemplate,
  templateNames,
  readJsonPath,
  headerValue,
  secretsMatch,
  automationVariableNames,
  writableColumns,
  planColumnValues,
  planRecordWrite,
  planResponseWrites,
  planWebhook,
  planEmail,
  MAIL_CONNECTION_MODES,
  blankMailAccount,
  mailPortForMode,
  normalizeMailSettings,
  restoreMailPasswords,
  resolveMailAccount,
  normalizeTriggerRecord,
  webhookPath,
  automationTypeLabel,
  automationTypeHelp,
  automationTypeShort,
  variableLocationLabel,
  summarizeAutomationRun,
  automationUsage,
  automationSheetUse,
  automationRecordVariables,
  PLAYWRIGHT_STEP_TYPES,
  buildHttpRequest,
  playwrightSteps
}
